import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { referrals, saveRef, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Copy, ShareNetwork, Gift, UserPlus, CalendarPlus, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ReferralLanding() {
  const { code } = useParams();
  saveRef(code);
  return <Navigate to="/register" replace/>;
}

export default function Referrals() {
  const { user } = useAuth();
  const isOwner = (user?.fleet_role || "owner") === "owner";
  const [data, setData] = useState(null);
  useEffect(() => {
    if (isOwner) { referrals.me().then(setData).catch((e) => toast.error(formatErr(e))); }
  }, [isOwner]);
  if (!isOwner) return <Navigate to="/app" replace/>;
  const link = data ? `${window.location.origin}/r/${data.code}` : "";

  return <AppShell>
    <div className="mb-6">
      <div className="eyebrow text-accent">Refer a hauler</div>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Earn a free month for every hauler you bring in</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Share your link. When someone signs up with it and starts a paid plan, your next renewal moves back one month. No limit.</p>
    </div>
    {!data ? <div className="text-muted-foreground" data-testid="referrals-loading">Loading…</div> : <>
      <ShareBox link={link} code={data.code}/>
      <Stats data={data}/>
      <HowItWorks/>
      <ReferralList items={data.referrals}/>
    </>}
  </AppShell>;
}

function ShareBox({ link, code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Couldn't copy. Press and hold the link to copy it."); }
  };
  const share = async () => {
    try { await navigator.share({ title: "HaulGuard", text: "I use HaulGuard for pickup & delivery inspections and handoff records. Try it free:", url: link }); }
    catch { /* user closed the share sheet */ }
  };
  return <section className="bg-primary text-primary-foreground p-5 border-l-8 border-accent mb-6" data-testid="referral-share">
    <div className="eyebrow opacity-70 mb-2">Your link · code <span className="font-mono-tech text-accent" data-testid="referral-code">{code}</span></div>
    <div className="flex flex-col sm:flex-row gap-2">
      <input readOnly value={link} data-testid="referral-link" onFocus={(e) => e.target.select()}
        className="flex-1 min-w-0 bg-white/10 border-2 border-white/20 px-3 py-2 font-mono-tech text-sm outline-none focus:border-accent"/>
      <button onClick={copy} data-testid="btn-copy-referral" className="btn-amber">{copied ? <Check size={18} weight="bold"/> : <Copy size={18} weight="bold"/>} {copied ? "Copied" : "Copy link"}</button>
      {typeof navigator !== "undefined" && navigator.share && <button onClick={share} data-testid="btn-share-referral" className="btn-amber"><ShareNetwork size={18} weight="bold"/> Share</button>}
    </div>
  </section>;
}

function Stats({ data }) {
  const cells = [["Signed up", data.signed_up, "ref-stat-signed"], ["Free months earned", data.rewarded, "ref-stat-earned"],
    ["Waiting for your plan", data.pending_months, "ref-stat-pending"]];
  return <div className="grid grid-cols-3 gap-2 mb-2">
    {cells.map(([k, v, tid]) => <div key={k} className="bg-white border border-border/60 p-4" data-testid={tid}>
      <div className="eyebrow text-muted-foreground">{k}</div>
      <div className="font-display font-black text-3xl">{v}</div>
    </div>)}
    {data.pending_months > 0 && <p className="col-span-3 text-xs text-muted-foreground" data-testid="ref-pending-note">
      {data.complimentary
        ? `Your account has complimentary access, so there's nothing to bill. ${data.pending_months === 1 ? "This free month is" : "These free months are"} kept on record.`
        : `You don't have a paid plan yet, so ${data.pending_months === 1 ? "this free month is" : "these free months are"} saved. They start the day you pick a plan, before your first charge.`}</p>}
  </div>;
}

function HowItWorks() {
  const steps = [[ShareNetwork, "Share your link", "Text it, post it in a hauler group, or hand it to a broker."],
    [UserPlus, "They sign up and pick a plan", "Their free trial works as usual. The reward counts once they start paying."],
    [CalendarPlus, "Your renewal moves back a month", "No charge that month. Every paid referral adds another."]];
  return <section className="grid md:grid-cols-3 gap-2 my-6">
    {steps.map(([Icon, t, d], i) => <div key={t} className="bg-white border border-border/60 p-4">
      <Icon size={26} weight="duotone" className="text-accent mb-2"/>
      <div className="font-display font-bold">{i + 1}. {t}</div>
      <div className="text-sm text-muted-foreground">{d}</div>
    </div>)}
  </section>;
}

function ReferralList({ items }) {
  return <section>
    <h2 className="font-display font-bold text-xl mb-3">Your referrals</h2>
    {items.length === 0
      ? <div className="border-2 border-dashed p-8 text-center text-muted-foreground" data-testid="referrals-empty">No one has signed up with your link yet.</div>
      : <ul className="space-y-2" data-testid="referrals-list">
          {items.map(r => <li key={r.id} data-testid={`referral-${r.id}`} className="flex flex-wrap items-center gap-3 bg-white border border-border/60 p-4">
            <Gift size={22} weight="duotone" className={r.status === "rewarded" ? "text-accent" : "text-muted-foreground"}/>
            <div className="flex-1 min-w-0">
              <div className="font-bold">{r.name}</div>
              <div className="font-mono-tech text-[11px] text-muted-foreground">Joined {new Date(r.joined_at).toLocaleDateString()}</div>
            </div>
            <span data-testid={`referral-status-${r.id}`} className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${r.status === "rewarded" ? "bg-accent text-accent-foreground" : "bg-secondary"}`}>
              {r.status === "rewarded" ? (r.reward === "banked" ? "Free month saved" : "Free month added") : "Signed up · not paying yet"}
            </span>
          </li>)}
        </ul>}
  </section>;
}
